import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useContexts } from "../features/contexts/hooks/useContexts";
import { Context } from "../features/contexts/models/Context";
import { useContextDetail } from "../features/contexts/hooks/useContextDetail";
import { orderContexts } from "../features/contexts/utils/contextLabels";
import { ROUTES } from "../routes";

/**
 * The chooser after a sign-in that reaches more than one context (PD-0033): one hairline row per
 * organization, the remembered one first. Picking the context the person is already in goes home;
 * any other switches (a full reload into that context).
 */
const ChooseContextPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detailOf = useContextDetail();
  const { contexts, activeKey, currentKey, loaded, switchingTo, switchFailed, switchTo } = useContexts();
  const ordered = orderContexts(contexts, activeKey ?? currentKey);

  const choose = (context: Context) => {
    if (context.key === currentKey) navigate(ROUTES.HOME, { replace: true });
    else switchTo(context);
  };

  return (
    <section className="mx-auto w-full max-w-xl px-4 py-12">
      <h1 className="text-2xl font-semibold text-grey-20 dark:text-white">{t("contexts.choose.title")}</h1>
      {switchFailed && (
        <p role="alert" className="mt-4 text-sm text-red-700 dark:text-red-400">
          {t("contexts.switchFailed")}
        </p>
      )}
      <ul
        aria-busy={!loaded}
        className="mt-6 divide-y divide-grey-90 border-y border-grey-90 dark:divide-grey-30 dark:border-grey-30"
      >
        {ordered.map((context) => {
          const isCurrent = context.key === currentKey;
          return (
            <li key={context.key}>
              <button
                type="button"
                data-context-key={context.key}
                disabled={switchingTo !== null}
                onClick={() => choose(context)}
                className="flex w-full items-center justify-between gap-4 px-2 py-4 text-left hover:bg-grey-95 disabled:opacity-60 dark:hover:bg-primary-20"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium text-grey-20 dark:text-white">
                    {context.organizationName}
                  </span>
                  <span className="block text-sm text-grey-40 dark:text-grey-80">{detailOf(context)}</span>
                </span>
                <span className="shrink-0 text-sm font-medium text-primary-40 dark:text-primary-90">
                  {isCurrent ? t("contexts.current") : t("contexts.switch")}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ChooseContextPage;
